import React from 'react';
import { Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface TransportCargoPairingOption {
  value: string;
  label: string;
}

interface Props {
  rowKey: string;
  productName: string;
  value: string | null;
  options: TransportCargoPairingOption[];
  onChange: (value: string | null) => void;
  /** Without a pair: the ZD line it is counted on for now (the card's „do przypisania”). */
  guessedLabel?: string | null;
  /** A line with 0 pcs carries nothing: no hint about a missing pair. */
  carriesGoods?: boolean;
}

/**
 * PR3 (decision 2026-10-06): „z pozycji ZD” of one ZK cargo line on a
 * „Fabryka → klient” truck — the ZD lines on this truck of the same product.
 * Without a pair the line gets no freight (a warning, never a block).
 */
const TransportCargoPairingField: React.FC<Props> = ({
  rowKey, productName, value, options, onChange, guessedLabel = null, carriesGoods = true,
}) => {
  const { t } = useTranslation(['transport']);
  return (
    <section className="transport-cargo-pairing" data-testid={`transport-cargo-pairing-${rowKey}`}>
      <Text type="secondary" className="transport-cargo-label">{t('transport:pairing.label')}</Text>
      <Select<string>
        allowClear
        value={value ?? undefined}
        options={options}
        placeholder={t('transport:pairing.placeholder')}
        notFoundContent={t('transport:pairing.noCandidates')}
        aria-label={t('transport:pairing.ariaLabel', { product: productName })}
        data-testid={`transport-cargo-pairing-select-${rowKey}`}
        onChange={(next) => onChange(next ?? null)}
        style={{ width: '100%' }}
      />
      {options.length === 0 ? (
        <Text type="warning">{t('transport:pairing.noCandidates')}</Text>
      ) : value || !carriesGoods ? null : guessedLabel ? (
        <Text type="warning" data-testid={`transport-cargo-pairing-guessed-${rowKey}`}>
          {t('transport:pairing.guessedHint', { line: guessedLabel })}
        </Text>
      ) : (
        <Text type="warning">{t('transport:pairing.unpairedHint')}</Text>
      )}
    </section>
  );
};

export default TransportCargoPairingField;
